import { useContext, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { WbSunny } from "@mui/icons-material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RopCard1102ProdutividadeEspecificaTaticaoAggregated } from "../../types";

export type SpecificProductivityTacticalChartProps = {
    data: RopCard1102ProdutividadeEspecificaTaticaoAggregated;
    viewMode: "cluster" | "powerPlant";
};

export default function SpecificProductivityTacticalChart({
    data,
    viewMode,
}: SpecificProductivityTacticalChartProps) {
    const chartRef = useRef(null);
    const { theme } = useContext(MainContext);

    const chartData =
        viewMode === "cluster"
            ? data.clusterAverages.map((item) => ({
                name: item.clusterName || "Sem Cluster",
                value: item.average,
            }))
            : data.powerPlantAverages.map((item) => ({
                name: item.usinaName,
                value: item.average,
            }));

    const xLabels = chartData.map((item) => item.name);
    const values = chartData.map((item) => item.value);

    const getColorByValue = (value: number) => {
        if (value > 1.1) return "#10b981";
        if (value >= 0.9) return "#f59e0b";
        return "#ef4444";
    };

    const dataWithColors = values.map((value) => {
        const color = getColorByValue(value);
        return {
            value,
            itemStyle: {
                color: color,
                borderRadius: [6, 6, 0, 0],
            },
            label: {
                color: color,
                fontSize: 12,
            },
        };
    });

    const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: {
            left: "8%",
            right: "8%",
            bottom: "10%",
            top: "8%",
            containLabel: false,
        },
        xAxis: {
            type: "category",
            data: xLabels,
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 13,
                margin: 12,
            },
        },
        yAxis: {
            type: "value",
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: false,
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 12,
                margin: 8,
            },
        },
        series: [
            {
                data: dataWithColors,
                type: "bar",
                barWidth: "30px",
                barGap: "30%",
                label: {
                    show: true,
                    position: "top",
                    fontSize: 14,
                    fontWeight: "bold",
                    formatter: (params: any) => {
                        if (typeof params.value === "number") {
                            return params.value > 0 ? params.value.toFixed(2) : "N/A";
                        }
                        return "N/A";
                    },
                    distance: 8,
                },
            },
        ],
        tooltip: {
            trigger: "axis",
            backgroundColor: theme.backgroundColor || "#1a1a1a",
            borderColor: theme.cardBorderColor || "#444",
            textStyle: {
                color: theme.primaryTextColor || "#fff",
            },
            formatter: (params: any) => {
                if (Array.isArray(params) && params[0]) {
                    return `${params[0].name}<br/>Produtividade: ${params[0].value > 0 ? params[0].value.toFixed(2) : "N/A"}`;
                }
                return "N/A";
            },
        },
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 2 }}>
                <WbSunny sx={{ color: "#FBBF24", fontSize: 20 }} />
                <Typography
                    sx={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: theme.primaryTextColor,
                    }}
                >
                    Produtividade Específica
                </Typography>
            </Box>
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {chartData.length === 0 ? (
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "14px",
                            fontStyle: "italic",
                        }}
                    >
                        Não há {viewMode === "cluster" ? "clusters" : "usinas"} para serem mostrados
                    </Typography>
                ) : (
                    <RopChart option={option} />
                )}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 2,
                    paddingTop: 2,
                    borderTop: `1px solid ${theme.cardBorderColor}`,
                }}
            >
                <Typography
                    sx={{
                        color: theme.secondaryTextColor,
                        fontWeight: "400",
                        fontSize: "15px",
                    }}
                >
                    Média Geral
                </Typography>
                <Typography
                    sx={{
                        color: theme.primaryTextColor,
                        fontWeight: "bold",
                        fontSize: "16px",
                    }}
                >
                    {(data.overallAverage ?? 0).toFixed(2)}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 1.5,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                    }}
                >
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            backgroundColor: "#10b981",
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "12px",
                            fontWeight: "400",
                        }}
                    >
                        Excelente (&gt;1.1)
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                    }}
                >
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            backgroundColor: "#f59e0b",
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "12px",
                            fontWeight: "400",
                        }}
                    >
                        Bom (0.9-1.1)
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                    }}
                >
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "3px",
                            backgroundColor: "#ef4444",
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "12px",
                            fontWeight: "400",
                        }}
                    >
                        Crítico (&lt;0.9)
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
